// slashMenuPlugin.js
// ProseMirror plugin that shows a searchable command palette when the user
// types "/" at the beginning of an empty block.
//
// Supported commands:
//   - Agentic Section
//   - Map
//
// The menu supports keyboard navigation (ArrowUp/Down, Enter, Escape) and
// live text filtering via the existing pm-slash-menu__search CSS class.

import {NodeSelection, Plugin, PluginKey, TextSelection} from "prosemirror-state";
import {insertMapNode} from "../helpers/insertMapNode";
import {generateUUID} from "../helpers/utils/ids";

export const slashMenuKey = new PluginKey("slashMenu");

const CLOSED_MENU_STATE = {
    open: false,
    anchorPos: null,
};

// ─── Commands ────────────────────────────────────────────────────────────────

const SLASH_COMMANDS = [
    {
        id: "agentic-section",
        label: "Agentic Section",
        description: "Insert an AI-powered analysis section",
        keywords: ["agentic", "ai", "analysis", "section"],
        run(view, menuState) {
            return runAddAgenticSectionCommand(view, menuState);
        },
    },
    {
        id: "map",
        label: "Map",
        description: "Insert an interactive map",
        keywords: ["map", "geo", "location", "chart"],
        run(view, menuState, opts) {
            const slashBlock = findSlashTextblockAtAnchor(view.state.doc, menuState.anchorPos);
            if (!slashBlock) return false;

            insertMapNode(view, {
                range: {from: slashBlock.blockPos, to: slashBlock.blockEnd},
                meta: [[slashMenuKey, {type: 'close'}]],
            });
            view.focus();
            return true;
        },
    },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function isSlashTriggerInEmptyTextblock(state, from, to, text) {
    if (text !== "/" || from !== to) return false;

    const $from = state.doc.resolve(from);
    if (!$from.parent.isTextblock) return false;

    const blockText = $from.parent.textContent || "";
    const isAtBlockStart = from === $from.start($from.depth);

    return isAtBlockStart && blockText.length === 0;
}

function findSlashTextblockAtAnchor(doc, anchorPos) {
    if (!doc || !anchorPos) return null;

    const safePos = Math.max(1, Math.min(anchorPos, doc.content.size));
    const $pos = doc.resolve(safePos);
    if (!$pos.parent.isTextblock) return null;

    const parent = $pos.parent;
    if ((parent.textContent || "") !== "/") return null;

    const blockPos = $pos.before($pos.depth);
    return {
        blockPos,
        blockEnd: blockPos + parent.nodeSize,
        slashPos: $pos.start($pos.depth),
    };
}

function focusAgenticInputAtPos(view, pos) {
    const tryFocus = () => {
        if (typeof view?.nodeDOM !== "function") return false;
        const nodeDom = view.nodeDOM(pos);
        if (!(nodeDom instanceof HTMLElement)) return false;
        const nodeView = nodeDom.pmViewDesc?.spec;
        if (nodeView && typeof nodeView.focusInput === "function") return nodeView.focusInput();
        const input = nodeDom.querySelector("textarea, input");
        if (!(input instanceof HTMLElement) || typeof input.focus !== "function") return false;
        input.focus();
        return true;
    };
    if (!tryFocus()) setTimeout(() => tryFocus(), 0);
}

function runAddAgenticSectionCommand(view, menuState) {
    const {state} = view;
    const slashBlock = findSlashTextblockAtAnchor(state.doc, menuState.anchorPos);
    if (!slashBlock) return false;

    const agenticSectionType = state.schema.nodes.agenticSection;
    if (!agenticSectionType) return false;

    const tr = state.tr;
    tr.replaceWith(slashBlock.blockPos, slashBlock.blockEnd, agenticSectionType.create({
        sectionId: generateUUID(),
    }));
    tr.setSelection(NodeSelection.create(tr.doc, slashBlock.blockPos));
    tr.setMeta(slashMenuKey, {type: "close"});

    view.dispatch(tr.scrollIntoView());
    view.focus();
    focusAgenticInputAtPos(view, slashBlock.blockPos);
    return true;
}

function findScrollParent(node) {
    let current = node?.parentElement;
    while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return current;
        current = current.parentElement;
    }
    return null;
}

function filterCommands(query) {
    if (!query) return SLASH_COMMANDS;
    const q = query.toLowerCase();
    return SLASH_COMMANDS.filter((cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.keywords.some((k) => k.includes(q))
    );
}

// ─── Plugin ───────────────────────────────────────────────────────────────────

export function slashMenuPlugin(opts = {}) {
    // opts.user — current user object; forwarded to command runners so they can
    // stamp ownerId on newly inserted nodes (e.g. the Map command).
    //
    // selectedIndex/currentQuery track which command is highlighted in the menu.
    // They're shared between `props.handleKeyDown` (ProseMirror's own keydown
    // handling, which fires while the editor's contenteditable still has focus —
    // e.g. before the search input's requestAnimationFrame(focus) resolves, or in
    // tests that drive the plugin without a real EditorView/DOM) and the `view()`
    // module's search-input keydown listener (which fires once focus has moved to
    // the menu's search box). Previously these lived in separate closures, so
    // handleKeyDown's Enter case couldn't actually run anything — it just
    // preventDefault()'d and returned true, silently swallowing the keystroke.
    let selectedIndex = 0;
    let currentQuery = "";

    const runSelectedCommand = (view) => {
        const menuState = slashMenuKey.getState(view.state);
        if (!menuState?.open) return false;

        const cmds = filterCommands(currentQuery);
        const cmd = cmds[selectedIndex];
        if (!cmd) return false;

        return cmd.run(view, menuState, opts) !== false;
    };

    return new Plugin({
        key: slashMenuKey,

        state: {
            init: () => CLOSED_MENU_STATE,
            apply(tr, prev) {
                const meta = tr.getMeta(slashMenuKey);
                if (meta?.type === "open") {
                    return {open: true, anchorPos: meta.anchorPos};
                }
                if (meta?.type === "close") {
                    return CLOSED_MENU_STATE;
                }

                if (!prev.open) return prev;
                if (!tr.selectionSet && !tr.docChanged) return prev;

                const info = findSlashTextblockAtAnchor(tr.doc, prev.anchorPos);
                if (!info) return CLOSED_MENU_STATE;

                return {open: true, anchorPos: info.slashPos + 1};
            },
        },

        props: {
            handleTextInput(view, from, to, text) {
                if (!isSlashTriggerInEmptyTextblock(view.state, from, to, text)) return false;

                const tr = view.state.tr.insertText("/", from, to);
                tr.setSelection(TextSelection.create(tr.doc, from + 1));
                tr.setMeta(slashMenuKey, {type: "open", anchorPos: from + 1});
                tr.setMeta("addToHistory", false);
                view.dispatch(tr);
                selectedIndex = 0;
                currentQuery = "";
                return true;
            },

            handleKeyDown(view, event) {
                const menuState = slashMenuKey.getState(view.state);
                if (!menuState?.open) return false;

                if (event.key === "Escape") {
                    event.preventDefault();
                    const tr = view.state.tr.setMeta(slashMenuKey, {type: "close"});
                    tr.setMeta("addToHistory", false);
                    view.dispatch(tr);
                    return true;
                }

                if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Tab") {
                    event.preventDefault();
                    const cmds = filterCommands(currentQuery);
                    if (event.key === "ArrowUp") {
                        selectedIndex = Math.max(selectedIndex - 1, 0);
                    } else {
                        selectedIndex = Math.min(selectedIndex + 1, cmds.length - 1);
                    }
                    return true;
                }

                if (event.key === "Enter") {
                    event.preventDefault();
                    runSelectedCommand(view);
                    return true;
                }

                return false;
            },
        },

        view(editorView) {
            // ── DOM construction ────────────────────────────────────────────
            const menu = document.createElement("div");
            menu.className = "pm-slash-menu";
            menu.style.display = "none";
            menu.setAttribute("role", "listbox");
            menu.setAttribute("aria-label", "Insert block");

            const searchInput = document.createElement("input");
            searchInput.type = "text";
            searchInput.placeholder = "Filter…";
            searchInput.className = "pm-slash-menu__search";
            searchInput.setAttribute("aria-label", "Search commands");
            menu.appendChild(searchInput);

            const listEl = document.createElement("div");
            listEl.className = "pm-slash-menu__list";
            menu.appendChild(listEl);

            document.body.appendChild(menu);

            // ── Rendering ───────────────────────────────────────────────────
            const renderItems = (query) => {
                const cmds = filterCommands(query);
                listEl.replaceChildren();
                if (cmds.length === 0) {
                    const empty = document.createElement("div");
                    empty.className = "pm-slash-menu__empty";
                    empty.textContent = "No commands found";
                    listEl.appendChild(empty);
                    return;
                }
                cmds.forEach((cmd, i) => {
                    const row = document.createElement("button");
                    row.type = "button";
                    row.className = "pm-slash-menu__item" + (i === selectedIndex ? " is-selected" : "");
                    row.setAttribute("role", "option");
                    row.setAttribute("data-cmd-id", cmd.id);

                    const labelEl = document.createElement("span");
                    labelEl.className = "pm-slash-menu__item-label";
                    labelEl.textContent = cmd.label;

                    const descEl = document.createElement("span");
                    descEl.className = "pm-slash-menu__item-desc";
                    descEl.textContent = cmd.description;

                    row.appendChild(labelEl);
                    row.appendChild(descEl);

                    row.addEventListener("mousedown", (e) => {
                        e.preventDefault();
                        const menuState = slashMenuKey.getState(editorView.state);
                        if (!menuState?.open) return;
                        cmd.run(editorView, menuState, opts);
                    });
                    listEl.appendChild(row);
                });
            };

            const showMenu = (anchorPos) => {
                const coords = editorView.coordsAtPos(anchorPos);
                menu.style.display = "block";
                menu.style.top = `${coords.bottom + 6}px`;
                menu.style.left = `${coords.left}px`;
                selectedIndex = 0;
                currentQuery = "";
                searchInput.value = "";
                renderItems("");
                // Focus the search input so keyboard works
                requestAnimationFrame(() => searchInput.focus());
            };

            const hideMenu = () => {
                menu.style.display = "none";
                selectedIndex = 0;
                currentQuery = "";
            };

            const runSelected = () => {
                runSelectedCommand(editorView);
                editorView.focus();
            };

            // ── Search input events ─────────────────────────────────────────
            searchInput.addEventListener("input", () => {
                currentQuery = searchInput.value;
                selectedIndex = 0;
                renderItems(currentQuery);
            });

            searchInput.addEventListener("keydown", (e) => {
                const cmds = filterCommands(currentQuery);
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    selectedIndex = Math.min(selectedIndex + 1, cmds.length - 1);
                    renderItems(currentQuery);
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    selectedIndex = Math.max(selectedIndex - 1, 0);
                    renderItems(currentQuery);
                } else if (e.key === "Enter") {
                    e.preventDefault();
                    runSelected();
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    const tr = editorView.state.tr.setMeta(slashMenuKey, {type: "close"});
                    tr.setMeta("addToHistory", false);
                    editorView.dispatch(tr);
                    // Defer focus so the dispatch fully processes before focus shifts
                    setTimeout(() => editorView.focus(), 0);
                }
            });

            // ── Outside click ───────────────────────────────────────────────
            const onDocumentMouseDown = (e) => {
                if (menu.contains(e.target)) return;
                const menuState = slashMenuKey.getState(editorView.state);
                if (!menuState?.open) return;
                const tr = editorView.state.tr.setMeta(slashMenuKey, {type: "close"});
                tr.setMeta("addToHistory", false);
                editorView.dispatch(tr);
                // Return focus to the editor when the user clicks elsewhere
                setTimeout(() => editorView.focus(), 0);
            };

            const onViewportChange = () => {
                const menuState = slashMenuKey.getState(editorView.state);
                if (!menuState?.open || !menuState.anchorPos) return;
                const coords = editorView.coordsAtPos(menuState.anchorPos);
                menu.style.top = `${coords.bottom + 6}px`;
                menu.style.left = `${coords.left}px`;
            };

            const scrollParent = findScrollParent(editorView.dom);
            document.addEventListener("mousedown", onDocumentMouseDown);
            window.addEventListener("resize", onViewportChange);
            window.addEventListener("scroll", onViewportChange, true);
            if (scrollParent) scrollParent.addEventListener("scroll", onViewportChange, {passive: true});

            return {
                update(_view, prevState) {
                    const menuState = slashMenuKey.getState(editorView.state);
                    const prevMenuState = slashMenuKey.getState(prevState);

                    if (menuState?.open && !prevMenuState?.open) {
                        showMenu(menuState.anchorPos);
                    } else if (!menuState?.open && prevMenuState?.open) {
                        hideMenu();
                    } else if (menuState?.open && menuState.anchorPos !== prevMenuState?.anchorPos) {
                        onViewportChange();
                    }
                },
                destroy() {
                    document.removeEventListener("mousedown", onDocumentMouseDown);
                    window.removeEventListener("resize", onViewportChange);
                    window.removeEventListener("scroll", onViewportChange, true);
                    if (scrollParent) scrollParent.removeEventListener("scroll", onViewportChange);
                    if (menu.parentNode) menu.parentNode.removeChild(menu);
                },
            };
        },
    });
}
